import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { AdPlatform, PlanType, WebsiteTemplateCategory, WebsiteType } from 'modules/Const';
import { CurrentBrand } from 'modules/Types';
import useBrand from './useBrand';
import useAppView from './useAppView';

/**
 * Enum for all modal types in the application
 */
export enum GlobalModalType {
    CREATE_CAMPAIGN = 'create_campaign',
    CREATE_WEBSITE = 'create_website',
    BRAND_SELECTOR = 'brand_selector',
}

/**
 * Base configuration for any modal
 */
interface BaseModalConfig {
    /** Whether the modal is currently open */
    isOpen: boolean;
    /** Source/context where the modal was opened from (for analytics) */
    source?: string;
    /** Additional metadata for the modal */
    metadata?: Record<string, any>;
    /** Callback when modal is closed */
    onClose?: () => void;
    /** Callback when modal action is completed */
    onComplete?: (result?: any) => void;
}

/**
 * Brand selector specific configuration
 */
interface BrandSelectorConfig extends BaseModalConfig {
    /** What action to take after brand selection */
    nextAction: 'create_campaign' | 'create_website';
    /** Custom action to execute after brand selection */
    customAction?: (brand: CurrentBrand) => void;
    /** Modal title */
    title?: string;
    /** Modal subtitle */
    subtitle?: string;
    /** Function to filter available brands */
    brandFilter?: (brand: CurrentBrand) => boolean;
}

/**
 * Campaign creation specific configuration
 */
interface CreateCampaignConfig extends BaseModalConfig {
    /** Pre-selected ad platform */
    adPlatform?: AdPlatform;
    /** Pre-selected brand for campaign creation */
    selectedBrand?: CurrentBrand;
    /** Whether to skip brand selection step */
    skipBrandSelection?: boolean;
}

/**
 * Website creation specific configuration
 */
interface CreateWebsiteConfig extends BaseModalConfig {
    /** Pre-selected website template category */
    templateCategory?: WebsiteTemplateCategory;
    /** Pre-selected website type */
    websiteType?: WebsiteType;
    /** Pre-selected brand for website creation */
    selectedBrand?: CurrentBrand;
    /** Whether to skip brand selection step */
    skipBrandSelection?: boolean;
}

/**
 * Union type for all modal configurations
 */
type ModalConfig =
    | BrandSelectorConfig
    | CreateCampaignConfig
    | CreateWebsiteConfig
    | BaseModalConfig;

/**
 * Global modal state interface
 */
interface GlobalModalState {
    /** Currently active modals mapped by type */
    activeModals: Partial<Record<GlobalModalType, ModalConfig>>;

    /** Modal stack for handling multiple modals */
    modalStack: GlobalModalType[];

    /** Currently selected brand for creation flows */
    selectedBrandForCreation: CurrentBrand | null;

    /** Whether brand selection is in progress */
    isBrandSelectionActive: boolean;
}

/**
 * Global modal actions interface
 */
interface GlobalModalActions {
    /**
     * Open a modal with specific configuration
     */
    openModal: <T extends ModalConfig>(type: GlobalModalType, config: T) => void;

    /**
     * Close a specific modal
     */
    closeModal: (type: GlobalModalType) => void;

    /**
     * Close all modals
     */
    closeAllModals: () => void;

    /**
     * Check if a modal is currently open
     */
    isModalOpen: (type: GlobalModalType) => boolean;

    /**
     * Get configuration for a specific modal
     */
    getModalConfig: <T extends ModalConfig>(type: GlobalModalType) => T | undefined;

    /**
     * Set the selected brand for creation flows
     */
    setSelectedBrandForCreation: (brand: CurrentBrand | null) => void;

    /**
     * Open brand selector with next action
     */
    openBrandSelector: (config: Omit<BrandSelectorConfig, 'isOpen'>) => void;

    /**
     * Handle brand selection and execute next action
     */
    handleBrandSelection: (brand: CurrentBrand) => void;

    /**
     * Convenience methods for common modal types
     */
    openCreateCampaign: (config?: Omit<CreateCampaignConfig, 'isOpen'>) => void;
    openCreateWebsite: (config?: Omit<CreateWebsiteConfig, 'isOpen'>) => void;

    /**
     * Register a custom modal component
     */
    registerCustomModal: (type: GlobalModalType, component: React.ComponentType<any>) => void;

    /**
     * Get the top modal in the stack
     */
    getTopModal: () => GlobalModalType | null;

    /**
     * Execute modal action and handle cleanup
     */
    executeModalAction: (type: GlobalModalType, action: string, payload?: any) => void;
}

/**
 * Combined interface for the global modal store
 */
type GlobalModalStore = GlobalModalState & GlobalModalActions;

/**
 * Helper function to determine if brand selector should be shown
 * Rules:
 * - If in team view and no current brand: show profile selector
 * - If in brand view, current brand will be automatically set: skip profile selector
 */
const shouldShowBrandSelector = (selectedBrand?: CurrentBrand, skipBrandSelection?: boolean): boolean => {
    if (skipBrandSelection) return false;

    const { isTeamView } = useAppView.getState();
    const { currentBrand } = useBrand.getState();
    const effectiveSelectedBrand = selectedBrand || currentBrand;

    return isTeamView() && !effectiveSelectedBrand;
};
/**
 * Global modal management store using Zustand
 * Handles all modal states, brand selection, and creation flows
 */
export const useGlobalModals = create<GlobalModalStore>()(
    immer((set, get) => {
        return {
            // Initial State
            activeModals: {},
            modalStack: [],
            selectedBrandForCreation: null,
            isBrandSelectionActive: false,

            // Actions
            openModal: <T extends ModalConfig>(type: GlobalModalType, config: T) => {
                set((state) => {
                    state.activeModals[type] = { ...config, isOpen: true };

                    // Add to modal stack if not already present
                    if (!state.modalStack.includes(type)) {
                        state.modalStack.push(type);
                    }
                });
            },

            closeModal: (type: GlobalModalType) => {
                set((state) => {
                    const config = state.activeModals[type];

                    // Call onClose callback if provided
                    if (config?.onClose) {
                        config.onClose();
                    }

                    // Remove from active modals
                    delete state.activeModals[type];

                    // Remove from modal stack
                    state.modalStack = state.modalStack.filter(modalType => modalType !== type);

                    // Clear brand selection if this was a brand selector
                    if (type === GlobalModalType.BRAND_SELECTOR) {
                        state.isBrandSelectionActive = false;
                    }
                });
            },

            closeAllModals: () => {
                set((state) => {
                    // Call onClose for all active modals
                    Object.entries(state.activeModals).forEach(([type, config]) => {
                        if (config?.onClose) {
                            config.onClose();
                        }
                    });

                    state.activeModals = {};
                    state.modalStack = [];
                    state.isBrandSelectionActive = false;
                });
            },

            isModalOpen: (type: GlobalModalType) => {
                return Boolean(get().activeModals[type]?.isOpen);
            },

            getModalConfig: <T extends ModalConfig>(type: GlobalModalType): T | undefined => {
                return get().activeModals[type] as T;
            },

            setSelectedBrandForCreation: (brand: CurrentBrand | null) => {
                set((state) => {
                    state.selectedBrandForCreation = brand;
                });
            },

            openBrandSelector: (config: Omit<BrandSelectorConfig, 'isOpen'>) => {
                set((state) => {
                    state.isBrandSelectionActive = true;
                    state.activeModals[GlobalModalType.BRAND_SELECTOR] = {
                        ...config,
                        isOpen: true,
                        title: config.title || 'Select a Profile',
                        subtitle: config.subtitle || 'Choose the profile you want to create content for.'
                    };

                    if (!state.modalStack.includes(GlobalModalType.BRAND_SELECTOR)) {
                        state.modalStack.push(GlobalModalType.BRAND_SELECTOR);
                    }
                });
            },

            handleBrandSelection: (brand: CurrentBrand) => {
                const { closeModal, openCreateCampaign, openCreateWebsite, setSelectedBrandForCreation } = get();
                const brandSelectorConfig = get().getModalConfig<BrandSelectorConfig>(GlobalModalType.BRAND_SELECTOR);

                if (!brandSelectorConfig) return;

                // Set the selected brand for creation context
                // NOTE: This sets currentBrand for creation flows but does NOT change app view
                // The BrandDropdown will only show this brand if user is actually in Brand View
                setSelectedBrandForCreation(brand);

                // Close brand selector
                closeModal(GlobalModalType.BRAND_SELECTOR);

                // Execute next action
                switch (brandSelectorConfig.nextAction) {
                    case 'create_campaign':
                        openCreateCampaign({
                            selectedBrand: brand,
                            skipBrandSelection: true,
                            source: brandSelectorConfig.source
                        });
                        break;

                    case 'create_website':
                        openCreateWebsite({
                            selectedBrand: brand,
                            skipBrandSelection: true,
                            source: brandSelectorConfig.source
                        });
                        break;

                }
            },

            openCreateCampaign: (config: Omit<CreateCampaignConfig, 'isOpen'> = {}) => {
                const { openModal, openBrandSelector } = get();

                // Check if we need to show brand selector first
                if (shouldShowBrandSelector(config.selectedBrand, config.skipBrandSelection)) {
                    openBrandSelector({
                        nextAction: 'create_campaign',
                        source: config.source || 'create_campaign_direct'
                    });
                    return;
                }

                // Get effective selected brand (from config or current brand)
                const { currentBrand } = useBrand.getState();
                const effectiveSelectedBrand = config.selectedBrand || currentBrand;

                openModal(GlobalModalType.CREATE_CAMPAIGN, {
                    ...config,
                    selectedBrand: effectiveSelectedBrand,
                    isOpen: true
                });
            },

            openCreateWebsite: (config: Omit<CreateWebsiteConfig, 'isOpen'> = {}) => {
                const { openModal, openBrandSelector } = get();

                // Check if we need to show brand selector first
                if (shouldShowBrandSelector(config.selectedBrand, config.skipBrandSelection)) {
                    openBrandSelector({
                        nextAction: 'create_website',
                        source: config.source || 'create_website_direct'
                    });
                    return;
                }

                // Get effective selected brand (from config or current brand)
                const { currentBrand } = useBrand.getState();
                const effectiveSelectedBrand = config.selectedBrand || currentBrand;

                openModal(GlobalModalType.CREATE_WEBSITE, {
                    ...config,
                    selectedBrand: effectiveSelectedBrand,
                    isOpen: true
                });
            },

            registerCustomModal: (type: GlobalModalType, component: React.ComponentType<any>) => {
                // This could be extended to register custom modal components
                // For now, we'll handle this through the CustomModalConfig
            },

            getTopModal: () => {
                const { modalStack } = get();
                return modalStack.length > 0 ? modalStack[modalStack.length - 1] : null;
            },

            executeModalAction: (type: GlobalModalType, action: string, payload?: any) => {
                const config = get().getModalConfig(type);

                switch (action) {
                    case 'complete':
                        if (config?.onComplete) {
                            config.onComplete(payload);
                        }
                        get().closeModal(type);
                        break;

                    case 'cancel':
                        get().closeModal(type);
                        break;

                    default:
                        console.warn(`Unknown modal action: ${action} for modal type: ${type}`);
                }
            }
        };
    })
);

/**
 * Convenience hooks for specific modal types
 */

/**
 * Hook for campaign creation modal
 */
export const useCampaignModal = () => {
    const {
        openCreateCampaign,
        closeModal,
        isModalOpen,
        getModalConfig
    } = useGlobalModals();

    return {
        openCampaignModal: openCreateCampaign,
        closeCampaignModal: () => closeModal(GlobalModalType.CREATE_CAMPAIGN),
        isCampaignModalOpen: isModalOpen(GlobalModalType.CREATE_CAMPAIGN),
        campaignModalConfig: getModalConfig<CreateCampaignConfig>(GlobalModalType.CREATE_CAMPAIGN)
    };
};

/**
 * Hook for website creation modal
 */
export const useWebsiteModal = () => {
    const {
        openCreateWebsite,
        closeModal,
        isModalOpen,
        getModalConfig
    } = useGlobalModals();

    return {
        openWebsiteModal: openCreateWebsite,
        closeWebsiteModal: () => closeModal(GlobalModalType.CREATE_WEBSITE),
        isWebsiteModalOpen: isModalOpen(GlobalModalType.CREATE_WEBSITE),
        websiteModalConfig: getModalConfig<CreateWebsiteConfig>(GlobalModalType.CREATE_WEBSITE)
    };
};

/**
 * Hook for brand selector modal
 */
export const useBrandSelectorModal = () => {
    const {
        openBrandSelector,
        closeModal,
        isModalOpen,
        getModalConfig,
        handleBrandSelection,
        isBrandSelectionActive
    } = useGlobalModals();

    return {
        openBrandSelector,
        closeBrandSelector: () => closeModal(GlobalModalType.BRAND_SELECTOR),
        isBrandSelectorOpen: isModalOpen(GlobalModalType.BRAND_SELECTOR),
        brandSelectorConfig: getModalConfig<BrandSelectorConfig>(GlobalModalType.BRAND_SELECTOR),
        handleBrandSelection,
        isBrandSelectionActive
    };
};

export default useGlobalModals;